import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  structured_product_id: z.coerce.number().nullable().optional(),
  structured_product_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  barrier_date: z
    .preprocess((val) => parseToDateValue(val), dateSchema)
    .optional(),
  recall_barrier: z.coerce.number().nullable().optional(),
  capital_risk_barrier: z.coerce.number().nullable().optional(),
  coupon_barrier: z.coerce.number().nullable().optional(),
  coupon_value: z.coerce.number().nullable().optional(),
  payment_date: z
    .preprocess((val) => parseToDateValue(val), dateSchema)
    .optional(),
});

const client = "a5";
const table_name = "a5_derivative_barriers";

export function A5DerivativeBarriersNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Structured product barrier",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="structured_product_name"
                  label={t(`${table_name}.columns.structured_product_name`, {
                    ns: "tables",
                    defaultValue: "Produit structuré",
                  })}
                  isRequired={true}
                  control={form.control}
                  base_url="a5/a5_static_data"
                  id_column="structured_product_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: "a5",
                    table_name: "a5_static_data",
                    language: "fr",
                    filters: () => {
                      const equals = { asset_class_2: "structured_product" };
                      return { equals };
                    },
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="barrier_date"
                    label={t(`${table_name}.columns.barrier_date`, {
                      ns: "tables",
                      defaultValue: "Date d'observation",
                    })}
                    control={form.control}
                  />
                  <DatePickerField
                    name="payment_date"
                    label={t(`${table_name}.columns.payment_date`, {
                      ns: "tables",
                      defaultValue: "Date de paiement",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="coupon_barrier"
                    label={t(`${table_name}.columns.coupon_barrier`, {
                      ns: "tables",
                      defaultValue: "Barrière du coupon",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="coupon_value"
                    label={t(`${table_name}.columns.coupon_value`, {
                      ns: "tables",
                      defaultValue: "Valeur du coupon",
                    })}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="pointer-events-none flex items-center">
                        <span className="text-default-400 text-small">%</span>
                      </div>
                    }
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="recall_barrier"
                    label={t(`${table_name}.columns.recall_barrier`, {
                      ns: "tables",
                      defaultValue: "Barrière de callabilité",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="capital_risk_barrier"
                    label={t(`${table_name}.columns.capital_risk_barrier`, {
                      ns: "tables",
                      defaultValue: "Barrière protection du capital",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["structured_product_id"],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />
        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
